'use client';
import React, { useState, useId, Suspense } from 'react';
import classNames from 'classnames';
import { hasCldImage, TypeBlockAccordionContent } from '@fm-nx/contentful-types';
import { useContentfulInspectorMode, useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { CldImage } from 'next-cloudinary';
import { AnimatePresence, motion } from 'framer-motion';
import { Icon } from '@fm-nx/svg';
import TextMarkdown from '../text-markdown/text-markdown';

const MIN_ITEMS = 2;
const MAX_ITEMS = 6;

type AccordionContentPropTypes = {
    blockAccordionContent: TypeBlockAccordionContent<'WITHOUT_UNRESOLVABLE_LINKS'>;
    numberOfItems?: number;
    mode?: 'dark' | 'light';
};

export const AccordionContent = (props: AccordionContentPropTypes) => {
    const { blockAccordionContent, numberOfItems, mode = 'light' } = props;
    const updatedBlock = useContentfulLiveUpdates(blockAccordionContent);
    const inspectorProps = useContentfulInspectorMode({ entryId: updatedBlock.sys.id });
    const { fields } = updatedBlock;
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const instanceId = useId();
    const isDark = mode === 'dark';

    if (!fields) return null;

    const { overline, headerText, paragraph, items } = fields;

    if (!items || items.length === 0) return null;

    const allValidItems = items.filter((item) => item && item.fields);

    if (allValidItems.length < MIN_ITEMS) return null;

    const itemCount = numberOfItems ? Math.max(MIN_ITEMS, Math.min(MAX_ITEMS, numberOfItems)) : undefined;
    const validItems = itemCount ? allValidItems.slice(0, itemCount) : allValidItems;

    const handleToggle = (index: number) => {
        setExpandedIndex(expandedIndex === index ? null : index);
    };

    return (
        <section>
            <div className="w-full mx-auto max-w-container">
                <div className="mx-6 py-14 md:mx-0">
                    <div className="flex flex-col md:grid md:grid-cols-14 md:gap-4">
                        <div className="col-span-14 md:col-span-12 md:col-start-2">
                            <div className="max-w-2xl space-y-6">
                                {overline && (
                                    <h2
                                        className={classNames('h5 overline', { 'text-white': isDark })}
                                        {...inspectorProps({ fieldId: 'overline' })}
                                    >
                                        {overline}
                                    </h2>
                                )}
                                {headerText && (
                                    <h3
                                        className={classNames('h-xl', { 'text-white': isDark })}
                                        {...inspectorProps({ fieldId: 'headerText' })}
                                    >
                                        {headerText}
                                    </h3>
                                )}
                                {paragraph && (
                                    <TextMarkdown
                                        className={classNames('text-xl', { 'text-white': isDark })}
                                        text={paragraph}
                                        mode={mode}
                                        {...inspectorProps({ fieldId: 'paragraph' })}
                                    />
                                )}
                            </div>

                            <div className="mt-10">
                                {validItems.map((item, index) => {
                                    if (!item?.fields) return null;
                                    return (
                                        <AccordionItem
                                            key={item.sys.id}
                                            item={item}
                                            index={index}
                                            isExpanded={expandedIndex === index}
                                            onToggle={handleToggle}
                                            instanceId={instanceId}
                                            isDark={isDark}
                                            mode={mode}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

type AccordionItemType = NonNullable<
    TypeBlockAccordionContent<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']['items']
>[number];

type AccordionItemProps = {
    item: NonNullable<AccordionItemType>;
    index: number;
    isExpanded: boolean;
    onToggle: (index: number) => void;
    instanceId: string;
    isDark: boolean;
    mode: 'dark' | 'light';
};

const AccordionItem = ({ item, index, isExpanded, onToggle, instanceId, isDark, mode }: AccordionItemProps) => {
    const updatedItem = useContentfulLiveUpdates(item);
    const itemInspectorProps = useContentfulInspectorMode({ entryId: updatedItem.sys.id });
    const panelId = `accordion-panel-${instanceId}-${index}`;
    const triggerId = `accordion-trigger-${instanceId}-${index}`;
    const { headline, bodyText, images } = updatedItem.fields;

    const validImages = images?.filter((img) => img?.fields && hasCldImage(img.fields));

    return (
        <div
            className={classNames('border-b', {
                'border-gray-500': isDark,
                'border-gray-300': !isDark,
            })}
        >
            <h4>
                <button
                    id={triggerId}
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    onClick={() => onToggle(index)}
                    className={classNames(
                        'flex w-full items-center justify-between py-10 text-left h-md transition-colors cursor-pointer',
                        {
                            'text-white': isDark,
                            'text-gray-900': !isDark,
                        }
                    )}
                >
                    <span {...itemInspectorProps({ fieldId: 'headline' })}>{headline}</span>
                    <span className="mx-4 flex-shrink-0" aria-hidden="true">
                        <Icon
                            name="caretThin"
                            color={isDark ? 'white' : 'black'}
                            direction={isExpanded ? 'up' : 'down'}
                        />
                    </span>
                </button>
            </h4>
            <AnimatePresence initial={false}>
                {isExpanded && (
                    <motion.div
                        id={panelId}
                        role="region"
                        aria-labelledby={triggerId}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                    >
                        <div className="pb-10">
                            <div className="flex flex-col md:flex-row gap-10">
                                {bodyText && (
                                    <div
                                        className={classNames('md:w-3/5', {
                                            'text-white': isDark,
                                        })}
                                    >
                                        <TextMarkdown
                                            className="p-xl"
                                            text={bodyText}
                                            mode={mode}
                                            {...itemInspectorProps({ fieldId: 'bodyText' })}
                                        />
                                    </div>
                                )}
                                {validImages && validImages.length > 0 && (
                                    <div className="flex gap-5 md:w-2/5">
                                        {validImages.map((img) => {
                                            if (!img?.fields) return null;
                                            const cloudinary = hasCldImage(img.fields)
                                                ? img.fields.cloudinaryMedia[0]
                                                : null;
                                            if (!cloudinary) return null;
                                            return (
                                                <Suspense key={img.sys.id} fallback={<div />}>
                                                    <div className="relative w-[328px] h-[192px] shadow-lg flex-shrink-0">
                                                        <CldImage
                                                            className="object-cover w-full h-full"
                                                            src={cloudinary.public_id}
                                                            alt={img.fields.altText || ''}
                                                            width={328}
                                                            height={192}
                                                            sizes="328px"
                                                        />
                                                    </div>
                                                </Suspense>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AccordionContent;
